import Button from 'admin/components/common/Button';
import { Hint, Select } from 'admin/components/common/Form';
import { TextAreaPure } from 'admin/components/common/Form/TextArea';
import { TextField } from 'admin/components/common/Form/TextField';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import type { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import Text from 'admin/components/common/Text';
import { useTheme } from 'admin/components/common/Theme';
import type { InspectorContextType } from 'admin/components/pages/Story/CardEditor/Inspector/InspectorContext';
import { getPFCompPropName } from 'admin/components/pages/Story/CardEditor/Inspector/PropField';
import { MODAL } from 'admin/constants/common';
import { useAdminSelector } from 'admin/reducers';
import { selectEditableElement, selectEditableSymbolInfo } from 'admin/reducers/card-editor-extra/selectors';
import { Flex } from 'antd';
import { UNIFORM_PROPS } from 'common/constants/component-props';
import React, { useState } from 'react';
import type { ComponentAITemplate } from 'types/story';
import { isSymbol } from 'common/utils/blocks/symbol';
import { ELEMENT_EDIT_MODE } from 'common/constants';
import { PREDEFINED_KEY_OPTIONS, texts } from './constants';
import { getDefaultType, getFilteredTypeOptions, getParentAITemplateValue } from './utils';
import { useCardAiTemplateKeys } from './hooks';

type ModalProps = ModalManagerProvidedProps<MODAL.EDIT_AI_TEMPLATE>;

export type EditAITemplateModalData = {
	onSave: InspectorContextType['onUpdate'];
};

const ModalContent: React.FC<ModalProps> = props => {
	const element = useAdminSelector(selectEditableElement);
	const currentAITemplate = element.uiConfig.componentProps[UNIFORM_PROPS.aiTemplate];
	const defaultAITemplate: ComponentAITemplate = { key: '', type: getDefaultType(element), prompt: '' };
	const symbolInfo = useAdminSelector(selectEditableSymbolInfo);

	const { theme } = useTheme();

	// Check if element is part of a group
	const groupElement = element.inheritance?.parent?.find(
		parent => getParentAITemplateValue(parent)?.type === 'group'
	);
	const isGroupElement = groupElement !== undefined;

	// Initialize with current AI template or default values
	const [aiTemplate, setAITemplate] = useState<ComponentAITemplate>({
		...(currentAITemplate || defaultAITemplate),
		...(isGroupElement ? { ref: getParentAITemplateValue(groupElement)?.key } : {}),
	});

	const activeAITemplateKeys = useCardAiTemplateKeys(aiTemplate.ref, element._id);

	// Get filtered type options based on element type
	const typeOptions = getFilteredTypeOptions(element);

	const handleSave = () => {
		const fieldName = getPFCompPropName(UNIFORM_PROPS.aiTemplate, 'uniform')({ path: element.path });
		props.data.onSave?.({ value: aiTemplate, name: fieldName });
		props.close();
	};

	const handleClear = () => {
		const fieldName = getPFCompPropName(UNIFORM_PROPS.aiTemplate, 'uniform')({ path: element.path });
		// Use null instead of undefined to explicitly override master symbol value, otherwise just remove the value
		const isSymbolElement = isSymbol(element);
		const isInstanceEditMode = symbolInfo.editMode === ELEMENT_EDIT_MODE.INSTANCE;
		props.data.onSave?.({ value: isSymbolElement && isInstanceEditMode ? null : undefined, name: fieldName });
		props.close();
	};

	const updateField = (field: keyof ComponentAITemplate, value: string) => {
		setAITemplate(prev => ({ ...prev, [field]: value }));
	};

	// Validation logic
	const isKeyDuplicate = Boolean(aiTemplate.key.trim()) && activeAITemplateKeys.has(aiTemplate.key.trim());
	const isKeyValid = Boolean(aiTemplate.key.trim()) && !isKeyDuplicate;
	const canSave = isKeyValid && Boolean(aiTemplate.prompt.trim());

	return (
		<>
			<ModalBody>
				<Flex vertical gap="small">
					<Text size={Text.size.subtitle} weight={Text.weight.semibold}>
						{texts.title}
					</Text>
					<Text size={Text.size.label}>{texts.description}</Text>
				</Flex>

				<Flex vertical gap="large" style={{ marginTop: 16 }}>
					{/* Type */}
					<Flex vertical gap="small">
						<Select
							label={{ children: texts.type.label }}
							value={aiTemplate.type}
							options={typeOptions}
							theme={theme}
							eventListeners={{
								onChange: (value: ComponentAITemplate['type']) => updateField('type', value),
							}}
						/>
						<Hint
							theme={theme}
							text={aiTemplate.type === 'group' ? texts.type.hintCollection : texts.type.hint}
						/>
					</Flex>

					{/* Unique Key */}
					<Flex vertical gap="small">
						<Select
							label={{ children: texts.uniqueKey.label }}
							value={aiTemplate.key}
							options={PREDEFINED_KEY_OPTIONS}
							placeholder={element.uiConfig.editorProps.name}
							theme={theme}
							isCreatable
							isClearable
							eventListeners={{
								onChange: (value: string | undefined) => updateField('key', value || ''),
							}}
							error={isKeyDuplicate ? texts.uniqueKey.errorDuplicate : ''}
						/>
						{!isKeyDuplicate && (
							<Hint
								theme={theme}
								text={
									activeAITemplateKeys.size > 0
										? texts.uniqueKey.hintWithExisting(Array.from(activeAITemplateKeys))
										: texts.uniqueKey.hint
								}
							/>
						)}
					</Flex>

					{/* Prompt */}
					<Flex vertical gap="small">
						<TextAreaPure
							label={{ children: texts.prompt.label }}
							value={aiTemplate.prompt}
							placeholder={texts.prompt.placeholder}
							rows={4}
							onChange={e => updateField('prompt', e.target.value)}
							theme={theme}
						/>
						<Hint theme={theme} text={texts.prompt.hint} />
					</Flex>

					{/* Reference */}
					{isGroupElement && (
						<Flex vertical gap="small">
							<TextField
								label={{ children: texts.groupRef.label }}
								value={aiTemplate.ref || ''}
								placeholder={texts.groupRef.placeholder}
								onChange={e => updateField('ref', e.target.value)}
								theme={theme}
								disabled
								readOnly
								isLabelUppercase={false}
								isRequired
							/>
							<Hint theme={theme} text={texts.groupRef.hint} />
						</Flex>
					)}
				</Flex>
			</ModalBody>
			<ModalFooter>
				<Flex gap="small" justify="flex-end">
					<Button view="primary" disabled={!canSave} onClick={handleSave}>
						{texts.buttons.save}
					</Button>
					{currentAITemplate !== undefined && (
						<Button view="danger" onClick={handleClear}>
							{texts.buttons.clear}
						</Button>
					)}
					<Button view="secondary-gray" onClick={props.close}>
						{texts.buttons.cancel}
					</Button>
				</Flex>
			</ModalFooter>
		</>
	);
};

const EditAITemplateModal: React.FC<ModalProps> = props => {
	return (
		<Modal theme="dark" maskColor="black" open={props.open} destroyOnHidden width={640} transitionName="">
			<ModalContent {...props} />
		</Modal>
	);
};

export default EditAITemplateModal;
